import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Grid,
  Tooltip,
  Fab,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { novidadesApi, Novidade, NovidadeRQ } from '../../utils/novidadesApi';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import DataTable, { Column } from './DataTable';

const NovidadesManagement: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [novidades, setNovidades] = useState<Novidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNovidade, setSelectedNovidade] = useState<Novidade | null>(null);
  const [formData, setFormData] = useState<NovidadeRQ>({
    titulo: '',
    descricao: '',
    conteudo: '',
    dataLancamento: undefined,
    destaque: false,
    ordem: 0,
    isBanner: false,
    bannerExpirationDate: undefined,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>('ordem');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadNovidades();
  }, []);

  const loadNovidades = async () => {
    try {
      setLoading(true);
      const data = await novidadesApi.getAll();
      setNovidades(data.sort((a, b) => b.ordem - a.ordem || (b.dataLancamento || '').localeCompare(a.dataLancamento || '')));
    } catch (error: any) {
      toast.error('Erro ao carregar novidades: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (novidade?: Novidade) => {
    if (novidade) {
      setSelectedNovidade(novidade);
      setFormData({
        titulo: novidade.titulo,
        descricao: novidade.descricao,
        conteudo: novidade.conteudo,
        dataLancamento: novidade.dataLancamento ? novidade.dataLancamento.split('T')[0] : undefined,
        destaque: novidade.destaque,
        ordem: novidade.ordem,
        isBanner: novidade.isBanner,
        bannerExpirationDate: novidade.bannerExpirationDate ? novidade.bannerExpirationDate.split('T')[0] : undefined,
      });
    } else {
      setSelectedNovidade(null);
      setFormData({
        titulo: '',
        descricao: '',
        conteudo: '',
        dataLancamento: undefined,
        destaque: false,
        ordem: 0,
        isBanner: false,
        bannerExpirationDate: undefined,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedNovidade(null);
    setFormData({
      titulo: '',
      descricao: '',
      conteudo: '',
      dataLancamento: undefined,
      destaque: false,
      ordem: 0,
      isBanner: false,
      bannerExpirationDate: undefined,
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.titulo.trim() || !formData.conteudo.trim()) {
        toast.error('Título e conteúdo são obrigatórios');
        return;
      }

      if (formData.isBanner && !formData.bannerExpirationDate) {
        toast.error('Data de expiração do banner é obrigatória quando a novidade é marcada como banner');
        return;
      }

      if (selectedNovidade) {
        await novidadesApi.update(selectedNovidade.id, formData);
        toast.success('Novidade atualizada com sucesso!');
      } else {
        await novidadesApi.create(formData);
        toast.success('Novidade criada com sucesso!');
      }

      handleCloseDialog();
      loadNovidades();
    } catch (error: any) {
      toast.error('Erro ao salvar novidade: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleDeleteClick = (novidade: Novidade) => {
    setSelectedNovidade(novidade);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedNovidade) return;

    try {
      await novidadesApi.delete(selectedNovidade.id);
      toast.success('Novidade excluída com sucesso!');
      setDeleteDialogOpen(false);
      setSelectedNovidade(null);
      loadNovidades();
    } catch (error: any) {
      toast.error('Erro ao excluir novidade: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const columns: Column<Novidade>[] = [
    {
      id: 'titulo',
      label: 'Título',
      minWidth: 200,
      format: (_value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.titulo}
          </Typography>
          {row.destaque && (
            <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          )}
        </Box>
      ),
    },
    {
      id: 'descricao',
      label: 'Descrição',
      minWidth: 200,
      format: (_value, row) => (
        <Typography variant="body2" color="text.secondary" sx={{ 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 300
        }}>
          {row.descricao || '-'}
        </Typography>
      ),
    },
    {
      id: 'dataLancamento',
      label: 'Data de Lançamento',
      minWidth: 150,
      format: (_value, row) => (
        <Typography variant="body2">
          {row.dataLancamento 
            ? new Date(row.dataLancamento).toLocaleDateString('pt-BR')
            : '-'}
        </Typography>
      ),
    },
    {
      id: 'ordem',
      label: 'Ordem',
      minWidth: 100,
      format: (_value, row) => (
        <Chip label={row.ordem} size="small" variant="outlined" />
      ),
    },
    {
      id: 'destaque',
      label: 'Destaque',
      minWidth: 100,
      format: (_value, row) => (
        <Chip
          label={row.destaque ? 'Sim' : 'Não'}
          color={row.destaque ? 'warning' : 'default'}
          sx={{
            color: theme.palette.mode === 'dark' ? '#000' : undefined
          }}
          size="small"
        />
      ),
    },
    {
      id: 'isBanner',
      label: 'Banner',
      minWidth: 100,
      format: (_value, row) => (
        <Chip
          label={row.isBanner ? 'Sim' : 'Não'}
          color={row.isBanner ? 'primary' : 'default'}
          sx={{
            color: theme.palette.mode === 'dark' ? '#000' : undefined
          }}
          size="small"
        />
      ),
    },
    {
      id: 'actions',
      label: 'Ações',
      minWidth: 120,
      align: 'right',
      format: (_value, row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(row)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Gerenciar Novidades
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined, textTransform: 'none' }}
        >
          Nova Novidade
        </Button>
      </Box>

      <DataTable<Novidade>
        columns={columns}
        rows={novidades.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        total={novidades.length}
        orderBy={orderBy}
        order={order}
        onPageChange={(p) => setPage(p)}
        onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }}
        onRequestSort={(col) => {
          if (orderBy === col) {
            setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
          } else {
            setOrderBy(col);
            setOrder('asc');
          }
        }}
      />

      {/* Dialog de criação/edição */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedNovidade ? 'Editar Novidade' : 'Nova Novidade'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Título"
              fullWidth
              required
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              variant="outlined"
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={2}
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              variant="outlined"
              helperText="Breve descrição da novidade"
            />
            <TextField
              label="Conteúdo"
              fullWidth
              required
              multiline
              rows={6}
              value={formData.conteudo}
              onChange={(e) => setFormData({ ...formData, conteudo: e.target.value })}
              variant="outlined"
              helperText="Conteúdo completo da novidade"
            />
            <TextField
              label="Data de Lançamento"
              type="date"
              fullWidth
              value={formData.dataLancamento || ''}
              onChange={(e) => setFormData({ ...formData, dataLancamento: e.target.value || undefined })}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Ordem"
                type="number"
                value={formData.ordem}
                onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                variant="outlined"
                inputProps={{ min: 0 }}
                sx={{ width: 150 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.destaque}
                    onChange={(e) => setFormData({ ...formData, destaque: e.target.checked })}
                    color="warning"
                  />
                }
                label="Destaque"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isBanner}
                    onChange={(e) => {
                      const isBanner = e.target.checked;
                      setFormData({ 
                        ...formData, 
                        isBanner,
                        // Limpar data de expiração se desmarcar banner
                        bannerExpirationDate: isBanner ? formData.bannerExpirationDate : undefined
                      });
                    }}
                    color="primary"
                  />
                }
                label="Banner"
              />
            </Box>
            {formData.isBanner && (
              <TextField
                label="Data de Expiração do Banner"
                type="date"
                fullWidth
                required
                value={formData.bannerExpirationDate || ''}
                onChange={(e) => setFormData({ ...formData, bannerExpirationDate: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                helperText="Data até quando o banner ficará ativo. Apenas uma novidade pode ser banner por vez."
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary" sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a novidade "{selectedNovidade?.titulo}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NovidadesManagement;

